import React, { useState, useEffect } from 'react';
import { BookOpen, CheckCircle, Play, ChevronDown, ChevronRight, GraduationCap, Award, TrendingUp } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface AcademyContentProps {
  animationDelay?: number;
}

const AcademyContent: React.FC<AcademyContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [completedLessons, setCompletedLessons] = useState(0);
  const [expandedModules, setExpandedModules] = useState<number[]>([0, 1]);
  const [activeLesson, setActiveLesson] = useState<{ module: number; lesson: number } | null>(null);
  const [progress, setProgress] = useState(0);

  const modules = [
    {
      title: 'Introduction',
      lessons: [
        { name: 'Welcome to the Course', completed: true },
        { name: 'About Your Instructor', completed: true },
        { name: 'Outline and Objectives', completed: true }
      ]
    },
    {
      title: 'Fundamentals',
      lessons: [
        { name: 'Concept 1', completed: true },
        { name: 'Concept 2', completed: false },
        { name: 'Concept 3', completed: false }
      ]
    },
    {
      title: 'Advanced Techniques',
      lessons: [
        { name: 'Deep Dive', completed: false },
        { name: 'Case Studies', completed: false }
      ]
    }
  ];

  useEffect(() => {
    // Animate completed lessons count
    const lessonInterval = setInterval(() => {
      setCompletedLessons(prev => {
        if (prev >= 4) return 4;
        return prev + 1;
      });
    }, 300);

    // Animate progress bar
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        if (prev >= 50) return 50;
        return prev + 2;
      });
    }, 50);

    // Simulate active lesson animation
    const lessonTimer = setTimeout(() => {
      setActiveLesson({ module: 1, lesson: 1 });
    }, animationDelay + 1500);

    return () => {
      clearInterval(lessonInterval);
      clearInterval(progressInterval);
      clearTimeout(lessonTimer);
    };
  }, [animationDelay]);

  const toggleModule = (index: number) => {
    setExpandedModules(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  return (
    <div 
      className="flex-1 flex flex-col overflow-hidden"
      style={{ 
        backgroundColor: isDark ? '#1A1A1A' : '#F5F5F5',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Header */}
      <div 
        className="flex items-center justify-between px-3 py-2 border-b"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          backgroundColor: isDark ? '#222222' : '#FFFFFF',
          animation: `fadeIn 0.3s ease-out ${animationDelay + 100}ms both`
        }}
      >
        <div className="flex items-center gap-2">
          <GraduationCap className="w-3 h-3" style={{ color: '#0d7680' }} />
          <span className="text-[10px] font-semibold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
            Story Academy
          </span>
        </div>
        <div className="flex items-center gap-1">
          <span 
            className="text-[7px] px-1.5 py-0.5 rounded-full flex items-center gap-0.5"
            style={{ 
              backgroundColor: '#10B98120',
              color: '#10B981'
            }}
          >
            <span className="w-1 h-1 rounded-full bg-[#10B981]" style={{ animation: 'pulse 1s ease-in-out infinite' }} />
            Learning
          </span>
        </div>
      </div>

      {/* Course Header */}
      <div 
        className="px-3 py-2 border-b"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          backgroundColor: isDark ? '#1F1F1F' : '#FAFAFA',
          animation: `fadeIn 0.3s ease-out ${animationDelay + 200}ms both`
        }}
      >
        <div className="flex items-center gap-2 mb-1">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{ 
              background: 'linear-gradient(135deg, #0d7680 0%, #0a5a62 100%)',
            }}
          >
            <BookOpen className="w-4 h-4 text-white" />
          </div>
          <div>
            <div className="text-[9px] font-semibold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
              Marketing Mastery
            </div>
            <div className="text-[7px]" style={{ color: isDark ? '#888888' : '#666666' }}>
              8 lessons • 2h 30min
            </div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-2">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[7px]" style={{ color: isDark ? '#888888' : '#666666' }}>Progress</span>
            <span className="text-[7px] font-medium" style={{ color: '#0d7680' }}>{progress}%</span>
          </div>
          <div 
            className="h-1 rounded-full overflow-hidden"
            style={{ backgroundColor: isDark ? '#333333' : '#E5E5E5' }}
          >
            <div 
              className="h-full rounded-full transition-all duration-300"
              style={{ 
                width: `${progress}%`,
                backgroundColor: '#0d7680'
              }}
            />
          </div>
        </div>
      </div>

      {/* Module List */}
      <div 
        className="flex-1 overflow-auto px-2 py-2"
        style={{ animation: `fadeIn 0.4s ease-out ${animationDelay + 300}ms both` }}
      >
        <div className="space-y-2">
          {modules.map((module, moduleIndex) => (
            <div 
              key={moduleIndex}
              className="rounded-lg overflow-hidden"
              style={{ 
                backgroundColor: isDark ? '#222222' : '#FFFFFF',
                border: `1px solid ${isDark ? '#333333' : '#E5E5E5'}`,
                animation: `scaleIn 0.3s ease-out ${animationDelay + 400 + moduleIndex * 100}ms both`
              }}
            >
              {/* Module Header */}
              <div 
                className="flex items-center justify-between px-2 py-1.5 cursor-pointer"
                onClick={() => toggleModule(moduleIndex)}
                style={{ 
                  backgroundColor: isDark ? '#2A2A2A' : '#F8F8F8',
                  borderBottom: expandedModules.includes(moduleIndex) 
                    ? `1px solid ${isDark ? '#333333' : '#E5E5E5'}` 
                    : 'none'
                }}
              >
                <div className="flex items-center gap-1.5">
                  <div className="w-4 h-4 flex items-center justify-center">
                    {expandedModules.includes(moduleIndex) ? (
                      <ChevronDown className="w-3 h-3" style={{ color: isDark ? '#888888' : '#666666' }} />
                    ) : (
                      <ChevronRight className="w-3 h-3" style={{ color: isDark ? '#888888' : '#666666' }} />
                    )}
                  </div>
                  <span className="text-[8px] font-medium" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
                    {module.title}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <CheckCircle 
                    className="w-3 h-3" 
                    style={{ 
                      color: module.lessons.every(l => l.completed) ? '#10B981' : (isDark ? '#444444' : '#CCCCCC')
                    }} 
                  />
                </div>
              </div>

              {/* Lessons */}
              {expandedModules.includes(moduleIndex) && (
                <div className="py-1">
                  {module.lessons.map((lesson, lessonIndex) => {
                    const isActive = activeLesson?.module === moduleIndex && activeLesson?.lesson === lessonIndex;
                    return (
                      <div 
                        key={lessonIndex}
                        className="flex items-center gap-2 px-2 py-1 mx-1 rounded transition-all"
                        style={{ 
                          backgroundColor: isActive 
                            ? (isDark ? '#0d768030' : '#0d768015')
                            : 'transparent',
                          animation: isActive ? 'pulse 2s ease-in-out infinite' : 'none'
                        }}
                      >
                        <div className="w-4 h-4 flex items-center justify-center">
                          {lesson.completed ? (
                            <CheckCircle 
                              className="w-3 h-3" 
                              style={{ color: '#10B981' }} 
                            />
                          ) : (
                            <Play 
                              className="w-2.5 h-2.5" 
                              style={{ color: isActive ? '#0d7680' : (isDark ? '#666666' : '#999999') }} 
                            />
                          )}
                        </div>
                        <span 
                          className="text-[7px]" 
                          style={{ 
                            color: lesson.completed 
                              ? (isDark ? '#888888' : '#666666')
                              : (isDark ? '#FFFFFF' : '#111111'),
                            textDecoration: lesson.completed ? 'line-through' : 'none'
                          }}
                        >
                          {lesson.name}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Stats Footer */}
      <div 
        className="grid grid-cols-3 gap-1 px-2 py-2 border-t"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          backgroundColor: isDark ? '#222222' : '#FFFFFF',
          animation: `fadeIn 0.4s ease-out ${animationDelay + 800}ms both`
        }}
      >
        <div 
          className="text-center p-1.5 rounded-lg"
          style={{ backgroundColor: isDark ? '#1A1A1A' : '#F8F8F8' }}
        >
          <div className="flex items-center justify-center gap-0.5 mb-0.5">
            <CheckCircle className="w-2.5 h-2.5" style={{ color: '#10B981' }} />
            <span className="text-[10px] font-bold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
              {completedLessons}
            </span>
          </div>
          <span className="text-[6px]" style={{ color: isDark ? '#666666' : '#888888' }}>Completed</span>
        </div>
        
        <div 
          className="text-center p-1.5 rounded-lg"
          style={{ backgroundColor: isDark ? '#1A1A1A' : '#F8F8F8' }}
        >
          <div className="flex items-center justify-center gap-0.5 mb-0.5">
            <Award className="w-2.5 h-2.5" style={{ color: '#F59E0B' }} />
            <span className="text-[10px] font-bold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
              2
            </span>
          </div>
          <span className="text-[6px]" style={{ color: isDark ? '#666666' : '#888888' }}>Badges</span>
        </div>
        
        <div 
          className="text-center p-1.5 rounded-lg"
          style={{ backgroundColor: isDark ? '#1A1A1A' : '#F8F8F8' }}
        >
          <div className="flex items-center justify-center gap-0.5 mb-0.5">
            <TrendingUp className="w-2.5 h-2.5" style={{ color: '#0d7680' }} />
            <span className="text-[10px] font-bold" style={{ color: isDark ? '#FFFFFF' : '#111111' }}>
              92%
            </span>
          </div>
          <span className="text-[6px]" style={{ color: isDark ? '#666666' : '#888888' }}>Score</span>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes scaleIn {
          from { opacity: 0; transform: scale(0.95); }
          to { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default AcademyContent;
